import {
  Component,
  effect,
  ElementRef,
  forwardRef,
  HostListener,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { TextComponent } from '../text/text.component';
import { AbstractFormControlComponent } from '../abstract-form-control.component';
import { ButtonDirective } from '@ps-gui/shared-core';

export interface DropdownOption<T> {
  label: string;
  value: T;
}

@Component({
  selector: 'shared-ui-dropdown',
  standalone: true,
  imports: [NgClass, TextComponent, ButtonDirective, ReactiveFormsModule],
  templateUrl: './dropdown.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DropdownComponent),
      multi: true,
    },
  ],
})
export class DropdownComponent<T> extends AbstractFormControlComponent<T> {
  readonly dropdownOptions = input.required<DropdownOption<T>[]>();
  readonly placeholder = input('Auswählen...');
  readonly enableSearch = input(false);
  readonly enableAddButton = input(false);
  readonly addInputPlaceholder = input('Neuer Wert...');
  readonly label = input('');
  readonly borderColorHex = input('#727777');
  readonly warningText = input('Ungültige Auswahl');
  readonly isInvalid = input<boolean>(false);
  readonly selectedOption = input<DropdownOption<T> | undefined>(undefined);
  readonly isSelected = input<boolean>(false);
  readonly sortOptions = input(true);

  valueAdded = output<DropdownOption<T>>();

  readonly menuButton = viewChild<ElementRef<HTMLButtonElement>>('menuButton');

  searchStringFormControl = this.formBuilder.nonNullable.control('');
  customValueFormControl = this.formBuilder.nonNullable.control('');

  readonly isOpened = signal(false);

  @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent): void {
    event.stopPropagation();

    if (event.key !== 'Escape') {
      return;
    }

    this.isOpened.set(false);
    const target = event.target as HTMLButtonElement;
    target.blur();
  }

  @HostListener('window:click', ['$event']) onClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!this.isOpened() || this.menuButton()?.nativeElement.contains(target)) return;
    this.isOpened.set(false);
  }

  constructor() {
    super();

    effect(() => {
      if (this.isSelected()) {
        this.isOpened.set(true);
        return;
      }

      this.isOpened.set(false);
    });
  }

  toggleMenu() {
    if (this.isDisabled()) return;
    this.isOpened.update((bool) => !bool);
    this.searchStringFormControl.reset('');
    this.customValueFormControl.reset('');
  }

  get filteredOptions() {
    const options = this.dropdownOptions().filter((x) =>
      x.label.toLowerCase().startsWith(this.searchStringFormControl.value.toLowerCase()),
    );

    if (this.sortOptions()) {
      return options.sort((a, b) => a.label.localeCompare(b.label));
    }

    return options;
  }

  addOption() {
    const value = this.customValueFormControl.value;
    this.valueAdded.emit({ label: value, value: value as T });
  }

  onValueSelected(option: DropdownOption<T>): void {
    this.onChange(option.value);
    this.onTouched();
  }
}
